'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

type VenueDetailBackButtonProps = {
  defaultHref?: string;
};

export function VenueDetailBackButton({ defaultHref = '/venues' }: VenueDetailBackButtonProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const fromParam = (searchParams.get('from') ?? '').trim().toLowerCase();

  const fallbackHref = useMemo(() => resolveFallbackHref(fromParam, defaultHref), [fromParam, defaultHref]);
  const [showBack, setShowBack] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sameOriginReferrer = isSameOriginReferrer(window.location.origin, document.referrer);
    setShowBack(Boolean(fromParam) || sameOriginReferrer);
  }, [fromParam]);

  if (!showBack) return null;

  return (
    <button
      type="button"
      onClick={() => {
        if (typeof window !== 'undefined' && window.history.length > 1) {
          router.back();
          return;
        }
        router.push(fallbackHref);
      }}
      className="inline-flex items-center gap-2 rounded-full border border-border/70 px-4 py-2 text-xs font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow"
    >
      <span aria-hidden="true">{'<'}</span>
      Back
    </button>
  );
}

function resolveFallbackHref(fromParam: string, defaultHref: string): string {
  if (fromParam.startsWith('home')) return '/';
  if (fromParam.startsWith('venue') || fromParam.startsWith('directory')) return '/venues';
  if (fromParam.startsWith('location')) return '/venues/locations';
  if (fromParam.startsWith('court-intelligence') || fromParam.startsWith('ci')) return '/court-intelligence';
  return defaultHref;
}

function isSameOriginReferrer(origin: string, referrer: string): boolean {
  if (!referrer) return false;
  try {
    const referrerUrl = new URL(referrer);
    return referrerUrl.origin === origin;
  } catch {
    return false;
  }
}
